// Mirror of backend/src/utils/transportCargoEffectiveQuantity.js (plan
// docs/plans/2026-10-13-ladunek-z-dostawy-i-ostrzezenia.md): a ZK cargo line
// of a DELIVERED truck with a confirmed delivery counts with what was
// delivered (weight, m², m³, quantities, the load table and the trailer);
// every other line counts with its plan. The plan itself stays on the DTO —
// the transport form edits the plan, never this view.
import { resolveStoredQuantitySet } from '../../utils/commercialQuantity';
import { roundMoney } from '../../utils/money';

interface DeliverableCargoItem {
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  deliveredQuantity?: number | null;
  deliveredQuantityPcs?: number | null;
  deliveredQuantityM2?: number | null;
  deliveredQuantityM3?: number | null;
  unit?: string;
  goodsValueTotal?: number | null;
}

export function hasConfirmedDelivery(item: DeliverableCargoItem, transportStatus?: string | null): boolean {
  const delivered = item.deliveredQuantity;
  return String(transportStatus || '').toUpperCase() === 'DELIVERED'
    && delivered !== null
    && delivered !== undefined
    && Number.isFinite(Number(delivered))
    && Number(delivered) >= 0;
}

// The entered goods value follows the delivery like the quantity (backend
// deliveredGoodsValue, review PR #326). No value or no plan stays as it is.
function deliveredGoodsValue(item: DeliverableCargoItem): number | null | undefined {
  const value = item.goodsValueTotal;
  const planned = Number(item.quantity);
  if (value === null || value === undefined || !Number.isFinite(Number(value)) || !(planned > 0)) return value;
  return roundMoney(Number(value) * Number(item.deliveredQuantity) / planned);
}

/** The line as it counts; `plannedQuantity` keeps the plan when the delivery counts. */
export function toEffectiveCargoItem<T extends DeliverableCargoItem>(
  item: T,
  transportStatus?: string | null,
): T & { plannedQuantity?: number } {
  if (!hasConfirmedDelivery(item, transportStatus)) return item;
  const set = resolveStoredQuantitySet(item as unknown as Record<string, unknown>, {
    prefix: 'deliveredQuantity',
    quantityField: 'deliveredQuantity',
  });
  return {
    ...item,
    plannedQuantity: Number(item.quantity),
    quantity: Number(item.deliveredQuantity),
    quantityPcs: set.pcs,
    quantityM2: set.m2,
    quantityM3: set.m3,
    goodsValueTotal: deliveredGoodsValue(item),
  };
}

export interface ShortDelivery {
  key: string;
  orderId: string;
  productName: string;
  unit: string;
  planned: number;
  delivered: number;
}

/** Lines of a delivered truck that delivered less than planned (incl. 0). */
export function findShortDeliveries(
  route: { status?: string | null; cargoItems: Array<DeliverableCargoItem & { id: string; orderId: string; productName: string }> },
): ShortDelivery[] {
  return route.cargoItems
    .filter((item) => hasConfirmedDelivery(item, route.status) && Number(item.deliveredQuantity) < Number(item.quantity) - 0.0005)
    .map((item) => ({
      key: item.id,
      orderId: item.orderId,
      productName: item.productName,
      unit: item.unit || '',
      planned: Number(item.quantity),
      delivered: Number(item.deliveredQuantity),
    }));
}
